import { useEffect, useRef } from 'react'

import useBodyScrollLock from '../useBodyScrollLock'

type Options = {
  skip?: boolean
}

/**
 * Disables scrolling on all DOM nodes (including body & document), _except_ for the target node that needs to remain
 * scrollable. Returns a react mutable ref object that may be attached to an HTML element, to specify this node.
 *
 * Compatible with iOS Safari, which does not respect setting the `overflow: hidden;` css property on the body to
 * disable scrolling.
 */
const useExclusivelyScrollableNode = <T extends HTMLElement>(
  options?: Options
) => {
  const elementRef = useRef<T>()

  const { enableBodyScroll, disableBodyScroll } = useBodyScrollLock()

  useEffect(() => {
    if (elementRef.current && options?.skip !== true) {
      disableBodyScroll(elementRef.current)
    }

    return () => {
      if (elementRef.current && options?.skip !== true) {
        enableBodyScroll(elementRef.current)
      }
    }
  }, [elementRef.current, options?.skip])

  return elementRef
}

export default useExclusivelyScrollableNode
